import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { selectStyles } from "../../ManageUserTab/types/SelectStyles";
import { IOption, ITeam } from "../../../interfaces/team.interface";
import { INewPrompt, IPrompt } from "../interfaces/prompt.interface";
import { FaTimes } from "react-icons/fa";

interface IAddPromptModalProps {
  setShowAddPromptModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleAddPrompt?: (newPrompt: INewPrompt) => void;
  // optional initial prompt for editing
  initialPrompt?: IPrompt | null;
  onSubmit?: (data: IPrompt | any) => void; // called for both create and update when provided
  darkMode: boolean;
  selectedTeam: ITeam | null;
  teamListOptions: IOption[];
}

const AddPromptModal: React.FC<IAddPromptModalProps> = ({
  setShowAddPromptModal,
  handleAddPrompt,
  darkMode,
  selectedTeam,
  teamListOptions,
  initialPrompt,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [team, setTeam] = useState<string | null>(
    initialPrompt?.team ? String(initialPrompt.team.id) : selectedTeam ? selectedTeam.id.toString() : "",
  );
  const [nameInput, setNameInput] = useState<string>(initialPrompt?.name || "");
  const [textInput, setTextInput] = useState<string>(initialPrompt?.text || "");
  const nameInputRef = useRef<HTMLInputElement>(null);
  const allTeamOptions = [
    { label: "My Prompts", value: "" },
    ...teamListOptions,
  ];
  const selectedTeamOption =
    allTeamOptions.find((o) => o.value === team) ?? null;

  const handleAdd = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      if (!nameInput) nameInputRef.current?.focus();
      return;
    }
    const newPrompt = {
      name: nameInput.trim(),
      text: textInput.trim(),
    };
    const payload = {
      ...newPrompt,
      team_id: team && team !== "" ? Number(team) : null,
      id: initialPrompt?.id,
    };
    if (initialPrompt && onSubmit) {
      onSubmit(payload as any);
    } else if (handleAddPrompt) {
      handleAddPrompt({ ...newPrompt, team_id: team && team !== "" ? Number(team) : null });
    }
    setShowAddPromptModal(false);
  };

  useEffect(() => {
    document.body.classList.add("overflow-hidden");

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  return (
    <div className="modal-overlay">
      <div className="modal-panel p-6 pb-4 w-[500px] relative">
        <h2 className="text-xl title-base-color text-center mb-1">
          {t("promptLibrary.addPrompts")}
        </h2>
        <button
          aria-label="Close"
          className="absolute right-2 top-3 z-10 modal-close-btn"
          onClick={() => setShowAddPromptModal(false)}
          type="button"
        >
          <FaTimes className="text-xl text-gray-500 hover:text-del-red" />
        </button>

        <form onSubmit={handleAdd} noValidate>
          <div className="mb-4">
            <label className="block title-base-color mb-1">
              {t("promptLibrary.title")}
              <span className="text-del-red dark:text-red-400">*</span>
            </label>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              required
              placeholder={t("promptLibrary.titlePlaceholder")}
              ref={nameInputRef}
              className={`w-full px-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 ${
                darkMode
                  ? "bg-gray-700 text-gray-300 border-gray-600 focus:ring-green-500"
                  : "bg-gray-200 text-gray-700 border-gray-300 focus:ring-green-400"
              }`}
            />
          </div>

          <div className="mb-4">
            <label className="block title-base-color mb-1">
              {t("promptLibrary.text")}
              <span className="text-del-red dark:text-red-400">*</span>
            </label>
            <textarea
              value={textInput}
              rows={4}
              onChange={(e) => setTextInput(e.target.value)}
              required
              placeholder={t("promptLibrary.textPlaceholder")}
              className={`text-sm p-2 rounded w-full border focus:outline-none focus:ring-2
                ${
                  darkMode
                    ? "bg-gray-700 text-gray-300 border-gray-600 focus:ring-green-500"
                    : "bg-gray-200 text-gray-700 border-gray-300 focus:ring-green-400"
                }`}
              style={{ resize: "none" }}
            />
          </div>

          <div className="mb-4 text-sm">
            <label className="block title-base-color mb-1 text-base">
              {t("adminPanel.columns.team")}
            </label>

            <Select
              value={selectedTeamOption}
              options={allTeamOptions}
              onChange={(opt) => setTeam(opt?.value ?? "")}
              classNamePrefix="react-select"
              styles={{
                ...selectStyles(darkMode),
                option: (base, { isFocused }) => ({
                  ...base,
                  backgroundColor: isFocused
                    ? darkMode
                      ? "#86BC25"
                      : "#86BC25"
                    : darkMode
                      ? "#374151"
                      : "#FFFFFF",
                  color: darkMode ? "#FFFFFF" : "#000000",
                }),
              }}
            />
          </div>

          <div className="flex justify-end space-x-4 mt-2 text-sm">
            <button
              type="button"
              onClick={() => setShowAddPromptModal(false)}
              className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            >
              {t("adminPanel.cancel")}
            </button>
            <button
              type="submit"
              disabled={!nameInput || !textInput}
              className={`px-4 py-2 ${nameInput && textInput ? "btn-green" : "btn-disabled"}`}
            >
              {initialPrompt ? t("adminPanel.save") : t("adminPanel.add")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { AddPromptModal };
